import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import api from '../services/api';

// Pop-up de plano vencido: aparece em toda tela quando a assinatura do tenant
// está vencida (data_vencimento < NOW() → status_vencimento='vencida' em
// /pagamento/minha-assinatura). O backend já bloqueia envio de campanhas e
// respostas da Concierge IA nesse estado — este modal reforça visualmente e
// direciona o cliente para a renovação.
//
// Comportamento: é FECHÁVEL (X), mas REAPARECE a cada troca de tela — o estado
// `fechado` é resetado quando a rota muda. Fica oculto na própria tela de
// renovação (/creditos) para não atrapalhar o cliente que já está renovando.
export default function PlanExpiredModal() {
  const [vencida, setVencida] = useState(false);
  const [plano, setPlano] = useState(null);
  const [fechado, setFechado] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Consulta o status da assinatura uma vez ao montar.
  useEffect(() => {
    let cancelado = false;
    api.get('/pagamento/minha-assinatura')
      .then(({ data }) => {
        if (cancelado) return;
        if (data?.status_vencimento === 'vencida') {
          setVencida(true);
          setPlano(data.plano || null);
        }
      })
      .catch(() => { /* silencioso — sem assinatura ou erro de rede */ });
    return () => { cancelado = true; };
  }, []);

  // Reabre o modal a cada mudança de rota (aparece "em toda tela que abrir").
  useEffect(() => { setFechado(false); }, [location.pathname]);

  if (!vencida || fechado) return null;
  // Não sobrepõe a própria tela de renovação.
  if (location.pathname.startsWith('/creditos')) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(15,17,32,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        background: '#1c1f2a', color: '#fff', borderRadius: 14,
        maxWidth: 440, width: '100%', padding: '28px 26px',
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)', position: 'relative',
      }}>
        <button
          onClick={() => setFechado(true)}
          aria-label="Fechar"
          style={{
            position: 'absolute', top: 14, right: 14, background: 'none',
            border: 'none', color: '#9ca3af', cursor: 'pointer', padding: 4,
          }}
        >
          <X size={18} />
        </button>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 48, height: 48, borderRadius: '50%',
          background: 'rgba(248,113,113,0.15)', marginBottom: 16,
        }}>
          <AlertTriangle size={24} color="#f87171" />
        </div>

        <h2 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 10px' }}>
          Seu plano está vencido
        </h2>

        <p style={{ fontSize: 14, lineHeight: 1.5, color: '#cbd5e1', margin: '0 0 8px' }}>
          {plano ? <>A assinatura <strong>{plano}</strong> venceu.</> : <>Sua assinatura venceu.</>}{' '}
          O envio de campanhas e as respostas da Concierge IA ficam pausados
          até a renovação.
        </p>
        <p style={{ fontSize: 13, lineHeight: 1.5, color: '#94a3b8', margin: '0 0 20px' }}>
          Seus contatos, campanhas e configurações <strong>continuam salvos</strong> —
          basta renovar para voltar a usar todos os serviços.
        </p>

        <button
          onClick={() => { setFechado(true); navigate('/creditos'); }}
          style={{
            width: '100%', background: '#22c55e', color: '#fff',
            border: 'none', borderRadius: 8, padding: '11px 16px',
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Renovar agora
        </button>
      </div>
    </div>
  );
}
